import AddIcons from '@/icons/AddIcons'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from 'vnetwork-ui'
import { VMInstanceConfigOption } from '..'
import VMSection from './VMSection'

interface VMInstanceProps {
    servicePriceModel: string
    isPOC?: boolean
    vmName: string
    data?: any[]
    onUpdate?: (data: any[]) => void
    configOptions?: Array<VMInstanceConfigOption>
    vmPackageType?: 'predefined' | 'custom'
}

const VMInstance: React.FC<VMInstanceProps> = ({
    servicePriceModel,
    isPOC = false,
    vmName,
    data = [],
    onUpdate,
    configOptions = [],
    vmPackageType = 'predefined',
}) => {
    const { t } = useTranslation()
    const [vmData, setVmData] = useState(data)
    useEffect(() => {
        setVmData(data)
    }, [data])

    // Phân loại và lọc dữ liệu theo loại VM
    const categorizedData = useMemo(() => {
        const result = {
            'vm-instance': [] as any[],
            'vm-lb': [] as any[],
            'vm-other': [] as any[],
        }

        const filteredData = vmData

        filteredData.forEach((item) => {
            const title = item?.title || item?.name || ''
            // First try exact match on title field
            if (title === 'vm-instance') {
                result['vm-instance'].push(item)
            } else if (title === 'vm-lb') {
                result['vm-lb'].push(item)
            } else if (title === 'vm-other') {
                result['vm-other'].push(item)
            }
        })

        return result
    }, [vmData])

    const handleAddNew = (type: 'vm-instance' | 'vm-lb' | 'vm-other') => {
        // Find the next available vmIndex
        const maxIndex = vmData.reduce(
            (max, item) => Math.max(max, item.vmIndex || 0),
            -1
        )
        const nextVmIndex = maxIndex + 1

        const newItem = {
            name: '',
            commitment_value: 1,
            commitment_price_per_unit: 0,
            unit: 'package',
            title: type,
            type: type,
            commitment_prepaid: true,
            statementable: false,
            vmIndex: nextVmIndex,
        }
        const updatedData = [...vmData, newItem]
        setVmData(updatedData)
        onUpdate?.(updatedData)
    }

    const handleEdit = (
        type: 'vm-instance' | 'vm-lb' | 'vm-other',
        index: number,
        updatedData?: any
    ) => {
        if (updatedData) {
            // Find the item in vmData by vmIndex instead of reference
            const itemToEdit = categorizedData[type][index]
            const flatIndex = vmData.findIndex(
                (item) => item.vmIndex === itemToEdit?.vmIndex
            )

            if (flatIndex !== -1) {
                const newData = [...vmData]
                // Preserve the vmIndex when updating
                newData[flatIndex] = {
                    ...updatedData,
                    vmIndex: itemToEdit.vmIndex,
                }
                setVmData(newData)
                onUpdate?.(newData)
            }
        }
    }

    const handleDelete = (
        type: 'vm-instance' | 'vm-lb' | 'vm-other',
        index: number
    ) => {
        const itemsOfType = categorizedData[type]
        const itemToDelete = itemsOfType[index]

        // Delete by vmIndex instead of reference
        const updatedData = vmData.filter(
            (item) => item.vmIndex !== itemToDelete?.vmIndex
        )
        setVmData(updatedData)
        onUpdate?.(updatedData)
    }

    return (
        <div className="space-y-2 mb-2">
            {/* Always show VM Instances section */}
            <VMSection
                servicePriceModel={servicePriceModel}
                isPOC={isPOC}
                title="VM Instances"
                type="vm-instance"
                items={categorizedData['vm-instance']}
                onAddNew={() => handleAddNew('vm-instance')}
                onEdit={(index, updatedData) =>
                    handleEdit('vm-instance', index, updatedData)
                }
                onDelete={(index) => handleDelete('vm-instance', index)}
                vmName={vmName}
                configOptions={configOptions}
                vmPackageType={vmPackageType}
            />

            {/* Only show Load Balancer section if there are LB items */}
            {categorizedData['vm-lb'].length > 0 && (
                <VMSection
                    servicePriceModel={servicePriceModel}
                    isPOC={isPOC}
                    title="Load Balancers"
                    type="vm-lb"
                    vmName={vmName}
                    items={categorizedData['vm-lb']}
                    onAddNew={() => handleAddNew('vm-lb')}
                    onEdit={(index, updatedData) =>
                        handleEdit('vm-lb', index, updatedData)
                    }
                    onDelete={(index) => handleDelete('vm-lb', index)}
                    configOptions={configOptions}
                />
            )}

            {/* Only show Other Services section if there are other items */}
            {categorizedData['vm-other'].length > 0 && (
                <VMSection
                    servicePriceModel={servicePriceModel}
                    isPOC={isPOC}
                    title="Other Services"
                    type="vm-other"
                    vmName={vmName}
                    items={categorizedData['vm-other']}
                    onAddNew={() => handleAddNew('vm-other')}
                    onEdit={(index, updatedData) =>
                        handleEdit('vm-other', index, updatedData)
                    }
                    onDelete={(index) => handleDelete('vm-other', index)}
                    configOptions={configOptions}
                />
            )}

            {/* Add buttons for LB and Other Services when they're not shown */}
            <div className="flex gap-2 justify-end">
                {categorizedData['vm-lb']?.length === 0 && (
                    <Button
                        size="small"
                        variant="text"
                        type="button"
                        startIcon={<AddIcons />}
                        onClick={() => handleAddNew('vm-lb')}
                        className="text-sm border-dashed border-gray-300 hover:border-green-400 hover:text-green-600"
                    >
                        {t('common.add')} {t('load_balancer')}
                    </Button>
                )}

                {categorizedData['vm-other']?.length === 0 && (
                    <Button
                        size="small"
                        variant="text"
                        type="button"
                        startIcon={<AddIcons />}
                        onClick={() => handleAddNew('vm-other')}
                        className="text-sm border-dashed border-gray-300 hover:border-orange-400 hover:text-orange-600"
                    >
                        {t('common.add')} {t('other services')}
                    </Button>
                )}
            </div>
        </div>
    )
}

export default VMInstance
